// components/CargosReserva.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Table,
  Button,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  Statistic,
  Divider,
  Popconfirm,
  Badge,
  Alert,
  Spin,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UnorderedListOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  LoadingOutlined,
  HomeOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CargosReserva = ({ 
  numReserva, 
  nombreCliente, 
  visible, 
  onClose,
  onCargoCreado 
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingCargos, setLoadingCargos] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [cargosMaestros, setCargosMaestros] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editandoCargo, setEditandoCargo] = useState(null);
  const [form] = Form.useForm();
  const [lineasReserva, setLineasReserva] = useState([]);
  const [habitacionesReserva, setHabitacionesReserva] = useState([]);
  const [series, setSeries] = useState([]);
  const [tiposIVA, setTiposIVA] = useState([]);
  const [fechaWarning, setFechaWarning] = useState(null);

  // Cargar cargos, catálogo de cargos y datos adicionales al abrir el modal
  useEffect(() => {
    if (visible && numReserva) {
      fetchCargos();
      fetchCargosMaestros();
      fetchLineasReserva();
      fetchSeries();
      fetchTiposIVA(); 
    }
  }, [visible, numReserva]);

  // Datos de línea seleccionada para validación de fechas
  const [lineaSeleccionada, setLineaSeleccionada] = useState(null);
  
  // Mapeo de habitaciones a líneas para carga automática
  const habitacionLineaMap = useMemo(() => {
    const map = {};
    lineasReserva.forEach(linea => {
      if (linea.NumHab) {
        map[linea.NumHab] = linea.Linea;
      }
    });
    return map;
  }, [lineasReserva]);

  const fetchCargos = async () => {
    setLoadingCargos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/cargosreserva/reservas/${numReserva}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCargos(response.data.data || []);
    } catch (error) {
      console.error('Error cargando cargos:', error);
      message.error('Error al cargar los cargos');
    } finally {
      setLoadingCargos(false);
    }
  };

  const fetchTiposIVA = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/tiposiva`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTiposIVA(response.data.data || []);
    } catch (error) {
      console.error('Error cargando tipos IVA:', error);
      message.error('Error al cargar tipos de IVA');
    }
  };  

  const fetchCargosMaestros = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/cargos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCargosMaestros(response.data.data || []);
    } catch (error) {
      console.error('Error cargando catálogo de cargos:', error);
      message.error('Error al cargar catálogo de cargos');
    }
  };

  const fetchLineasReserva = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/reservas/${numReserva}/lineas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const lineas = response.data.data || [];
      setLineasReserva(lineas);
      
      // Extraer habitaciones únicas de las líneas
      const habitacionesUnicas = [...new Set(lineas
        .filter(l => l.NumHab)
        .map(l => l.NumHab)
      )].sort((a, b) => a - b);
      
      setHabitacionesReserva(habitacionesUnicas);
    } catch (error) {
      console.error('Error cargando líneas de reserva:', error);
      message.error('Error al cargar las líneas de reserva');
    }
  };

  const fetchSeries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/series`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSeries(response.data.data || []);
    } catch (error) {
      console.error('Error cargando series:', error);
      message.error('Error al cargar series');
    }
  };

  const handleNuevoCargo = () => {
    setEditandoCargo(null);
    setFechaWarning(null);
    form.resetFields();
    
    // Obtener valores por defecto
    const primeraHabitacion = habitacionesReserva.length > 0 ? habitacionesReserva[0] : null;
    const primeraLinea = lineasReserva.length > 0 ? lineasReserva[0] : null;
    
    // Establecer valores por defecto
    const today = dayjs();
    const tomorrow = dayjs().add(1, 'day');
    
    form.setFieldsValue({
      Codigo: '',
      Seccion: 'HOT',
      Descripcion: '',
      PVP: 0,
      NumCargos: 1,
      Fecha: today,
      FechaHasta: tomorrow,
      habitacion: primeraHabitacion,
      NLinea: primeraLinea ? primeraLinea.Linea : 1,
      Ocupante: 1,
      TipoIVA: '4',
      Aplicado: 'I',
      Serie: 'B',
      CalculaDto: true,
      Observaciones: '',
      Traspasa: false,
      Facturado: false,
      esDescuentoDia: false,
      esCortaEstancia: false,
      esCortaEstanciaP: false,
      esTipoHabEspecial: false,
      CalculoPrecio: false
    });
    
    // Si hay habitación, intentar establecer línea automáticamente
    if (primeraHabitacion && habitacionLineaMap[primeraHabitacion]) {
      form.setFieldsValue({
        NLinea: habitacionLineaMap[primeraHabitacion]
      });
      const lineaCorrespondiente = lineasReserva.find(l => l.Linea === habitacionLineaMap[primeraHabitacion]);
      setLineaSeleccionada(lineaCorrespondiente);
    } else if (primeraLinea) {
      setLineaSeleccionada(primeraLinea);
    }
    
    setModalVisible(true);
  };

  const handleEditarCargo = (cargo) => {
    setEditandoCargo(cargo);
    setFechaWarning(null);
    
    // Encontrar la línea correspondiente al cargo
    const lineaCorrespondiente = lineasReserva.find(l => l.Linea === cargo.NLinea);
    setLineaSeleccionada(lineaCorrespondiente);
    
    form.setFieldsValue({
      ...cargo,
      Codigo: cargo.Codigo || '',
      Seccion: cargo.Seccion || 'HOT',
      Descripcion: cargo.Descripcion || '',
      PVP: parseFloat(cargo.PVP) || 0,
      NumCargos: parseInt(cargo.NumCargos) || 1,
      Fecha: cargo.Fecha ? dayjs(cargo.Fecha) : dayjs(),
      FechaHasta: cargo.FechaHasta ? dayjs(cargo.FechaHasta) : dayjs().add(1, 'day'),
      habitacion: cargo.habitacion || null,
      NLinea: cargo.NLinea || 1,
      Ocupante: cargo.Ocupante || 1,
      TipoIVA: cargo.TipoIVA || '4',
      Aplicado: cargo.Aplicado || 'I',
      Serie: cargo.Serie || 'B',
      CalculaDto: cargo.CalculaDto !== false,
      Observaciones: cargo.Observaciones || '',
      Traspasa: Boolean(cargo.Traspasa),
      Facturado: Boolean(cargo.Facturado),
      esDescuentoDia: Boolean(cargo.esDescuentoDia),
      esCortaEstancia: Boolean(cargo.esCortaEstancia),
      esCortaEstanciaP: Boolean(cargo.esCortaEstanciaP),
      esTipoHabEspecial: Boolean(cargo.esTipoHabEspecial),
      CalculoPrecio: Boolean(cargo.CalculoPrecio)
    });
    
    setModalVisible(true);
  };

  const calcularDias = () => {
    const fecha = form.getFieldValue('Fecha');
    const fechaHasta = form.getFieldValue('FechaHasta');
    
    if (fecha && fechaHasta) {
      const dias = fechaHasta.diff(fecha, 'days') + 1;
      return dias > 0 ? dias : 1;
    }
    return 1;
  };

  const calcularTotal = () => {
    const pvp = parseFloat(form.getFieldValue('PVP') || 0);
    const numCargos = parseInt(form.getFieldValue('NumCargos') || 1);
    const dias = calcularDias();
    
    return (pvp * numCargos * dias).toFixed(2);
  };

  // Validar fechas con respecto a la línea/habitación seleccionada
  const validarFechas = () => {
    const fecha = form.getFieldValue('Fecha');
    const fechaHasta = form.getFieldValue('FechaHasta');
    const habitacion = form.getFieldValue('habitacion');
    const nLinea = form.getFieldValue('NLinea');
    
    if (!fecha || !fechaHasta) {
      setFechaWarning(null);
      return false;
    }
    
    // Si hay línea seleccionada, verificar fechas
    if (nLinea && lineaSeleccionada) {
      const fechaInicioLinea = dayjs(lineaSeleccionada.Entrada);
      const fechaFinLinea = dayjs(lineaSeleccionada.Salida);
      
      // Verificar si las fechas están dentro del rango de la línea
      const estaDentro = fecha.isSameOrAfter(fechaInicioLinea, 'day') && 
                        fechaHasta.isSameOrBefore(fechaFinLinea, 'day');
      
      if (!estaDentro) {
        setFechaWarning({
          type: 'warning',
          message: `Las fechas seleccionadas están fuera del rango de la línea ${nLinea} (${fechaInicioLinea.format('DD/MM/YYYY')} - ${fechaFinLinea.format('DD/MM/YYYY')})`
        });
      } else {
        setFechaWarning(null);
      }
      
      return estaDentro;
    }
    
    setFechaWarning(null);
    return true;
  };

  // Manejar cambio de habitación
  const handleCambioHabitacion = (habitacion) => {
    if (habitacion && habitacionLineaMap[habitacion]) {
      // Buscar la línea correspondiente a la habitación
      const lineaCorrespondiente = habitacionLineaMap[habitacion];
      form.setFieldsValue({
        NLinea: lineaCorrespondiente
      });
      
      // Establecer la línea seleccionada para validación
      const linea = lineasReserva.find(l => l.Linea === lineaCorrespondiente);
      setLineaSeleccionada(linea);
      
      // Validar fechas después de cambiar la línea
      setTimeout(() => validarFechas(), 100);
    }
  };

  // Manejar cambio de línea
  const handleCambioLinea = (nLinea) => {
    if (nLinea) {
      // Buscar la línea en el array de líneas
      const linea = lineasReserva.find(l => l.Linea === nLinea);
      setLineaSeleccionada(linea);
      
      // Si la línea tiene habitación asignada, actualizar el campo de habitación
      if (linea && linea.NumHab) {
        form.setFieldsValue({
          habitacion: linea.NumHab
        });
      }
      
      // Validar fechas después de cambiar la línea
      setTimeout(() => validarFechas(), 100);
    } else {
      setLineaSeleccionada(null);
    }
  };

  const handleGuardarCargo = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Validar que se haya seleccionado línea
      if (!values.NLinea) {
        message.error('La línea es obligatoria');
        setLoading(false);
        return;
      }
      
      // Validar fechas
      const fechaValida = validarFechas();
      if (!fechaValida && fechaWarning) {
        const confirm = await new Promise((resolve) => {
          Modal.confirm({
            title: 'Advertencia de Fechas',
            content: fechaWarning.message + '. ¿Desea continuar de todos modos?',
            okText: 'Sí, continuar',
            cancelText: 'No, corregir',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        
        if (!confirm) {
          setLoading(false);
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      
      // Calcular días reales y total
      const diasReales = calcularDias();
      const total = parseFloat(calcularTotal());
      
      const datosCargo = {
        ...values,
        NumReserva: numReserva,
        PVP: parseFloat(values.PVP),
        NumCargos: parseInt(values.NumCargos),
        Fecha: values.Fecha.format('YYYY-MM-DD'),
        FechaHasta: values.FechaHasta.format('YYYY-MM-DD'),
        habitacion: values.habitacion || null,
        Traspasa: Boolean(values.Traspasa),
        Facturado: Boolean(values.Facturado),
        CalculaDto: Boolean(values.CalculaDto),
        esDescuentoDia: Boolean(values.esDescuentoDia),
        esCortaEstancia: Boolean(values.esCortaEstancia),
        esCortaEstanciaP: Boolean(values.esCortaEstanciaP),
        esTipoHabEspecial: Boolean(values.esTipoHabEspecial),
        CalculoPrecio: Boolean(values.CalculoPrecio),
        DiasReales: diasReales,
        Total: total,
        // Si el cargo maestro tiene descripción, usarla
        Descripcion: values.Descripcion || (
          cargosMaestros.find(c => c.Codigo === values.Codigo)?.Descripcion || ''
        )
      };

      if (editandoCargo) {
        // Actualizar cargo existente
        await axios.put(
          `${BASE_URL}/api/cargosreserva/${editandoCargo.i}`,
          datosCargo,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Cargo actualizado correctamente');
      } else {
        // Crear nuevo cargo
        await axios.post(
          `${BASE_URL}/api/cargosreserva`,
          datosCargo,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Cargo creado correctamente');
      }

      // Recargar lista de cargos
      fetchCargos();
      setModalVisible(false);
      
      // Notificar al componente padre si es necesario
      if (onCargoCreado) {
        onCargoCreado();
      }
      
    } catch (error) {
      console.error('Error guardando cargo:', error);
      message.error(error.response?.data?.error || 'Error al guardar el cargo');
    } finally {
      setLoading(false);
    }
  };

  // Función para validar fecha cuando cambia
  const handleFechaChange = () => {
    setTimeout(() => validarFechas(), 100);
  };

  const handleEliminarCargo = async (cargoId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/cargosreserva/${cargoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      message.success('Cargo eliminado correctamente');
      fetchCargos();
      
    } catch (error) {
      console.error('Error eliminando cargo:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el cargo');
    } finally {
      setLoading(false);
    }
  };

  const calcularTotalCargos = () => {
    return cargos.reduce((total, cargo) => total + parseFloat(cargo.Total || 0), 0);
  };

  // Cuando cambia el código de cargo, cargar automáticamente la descripción y PVP
  const handleCambioCodigo = (codigo) => {
    if (codigo) {
      const cargoMaestro = cargosMaestros.find(c => c.codigo === codigo);
      if (cargoMaestro) {
        form.setFieldsValue({
          Descripcion: cargoMaestro.descripcion,
          PVP: parseFloat(cargoMaestro.precio) || 0,
          Seccion: cargoMaestro.seccion || 'HOT'
        });
      }
    }
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 100,
      render: (codigo) => <Tag color="blue">{codigo}</Tag>,
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'PVP',
      dataIndex: 'PVP',
      key: 'PVP',
      width: 100,
      render: (pvp) => `${parseFloat(pvp).toFixed(2)} €`,
    },
    {
      title: 'Cantidad',
      dataIndex: 'NumCargos',
      key: 'NumCargos',
      width: 90,
    },
    {
      title: 'Fechas',
      key: 'fechas',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <CalendarOutlined /> Desde: {dayjs(record.Fecha).format('DD/MM/YY')}
          </div>
          <div style={{ fontSize: '12px' }}>
            <CalendarOutlined /> Hasta: {dayjs(record.FechaHasta).format('DD/MM/YY')}
          </div>
          <Tag size="small">Días: {record.DiasReales}</Tag>
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'Total',
      key: 'Total',
      width: 120,
      render: (total) => (
        <Text strong style={{ color: '#1890ff' }}>
          {parseFloat(total).toFixed(2)} €
        </Text>
      ),
    },
    {
      title: 'Habitación',
      dataIndex: 'habitacion',
      key: 'habitacion',
      width: 100,
      render: (habitacion) => habitacion ? <Tag color="green">{habitacion}</Tag> : '-',
    },
    {
      title: 'Línea/Ocupante',
      key: 'lineaOcupante',
      width: 120,
      render: (_, record) => (
        <span>
          L{record.NLinea}-O{record.Ocupante}
        </span>
      ),
    },
    {
      title: 'IVA',
      key: 'iva',
      width: 100,
      render: (_, record) => {
        const tipoIVA = tiposIVA.find(t => t.Codigo === record.TipoIVA);
        const porcentaje = tipoIVA ? tipoIVA.ValorActual : record.TipoIVA;
      
        return (
          <div>
            <Tag>{porcentaje}%</Tag>
            <Tag color={record.Aplicado === 'I' ? 'green' : 'orange'}>
              {record.Aplicado === 'I' ? 'Incl.' : 'Exento'}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Facturado',
      dataIndex: 'Facturado',
      key: 'Facturado',
      width: 100,
      render: (facturado) => (
        <Badge
          status={facturado ? 'success' : 'default'}
          text={facturado ? 'Sí' : 'No'}
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditarCargo(record)}
              disabled={record.Facturado || record.nfactura > 0}
            />
          </Tooltip>
          
          <Tooltip title="Eliminar">
            <Popconfirm
              title="¿Eliminar este cargo?"
              description="Esta acción no se puede deshacer"
              onConfirm={() => handleEliminarCargo(record.i)}
              okText="Sí"
              cancelText="No"
              okButtonProps={{ danger: true }}
              disabled={record.Facturado || record.nfactura > 0}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                disabled={record.Facturado || record.nfactura > 0}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* Modal principal de cargos */}
      <Modal
        title={
          <Space>
            <DollarOutlined />
            <span>Cargos - Reserva #{numReserva}</span>
            <Tag color="blue">{nombreCliente}</Tag>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1400}
        footer={[
          <Button key="close" onClick={onClose}>
            Cerrar
          </Button>
        ]}
      >
        {/* Resumen de cargos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title="Total Cargos"
              value={calcularTotalCargos()}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix="€"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Número de Cargos"
              value={cargos.length}
              prefix={<UnorderedListOutlined />}
            />
          </Col>
        </Row>

        {/* Botón para añadir cargo */}
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNuevoCargo}
          >
            Añadir Cargo
          </Button>
          
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchCargos();
              fetchLineasReserva();
            }}
            style={{ marginLeft: 8 }}
            loading={loadingCargos}
          >
            Actualizar
          </Button>
        </div>

        {/* Tabla de cargos */}
        <Table
          columns={columns}
          dataSource={cargos}
          rowKey="i"
          loading={loadingCargos}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1300 }}
          size="small"
          summary={() => (
            cargos.length > 0 && (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={7}>
                    <Text strong>Total:</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong style={{ color: '#1890ff' }}>
                      {calcularTotalCargos().toFixed(2)} €
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} colSpan={4} />
                </Table.Summary.Row>
              </Table.Summary>
            )
          )}
        />

        {/* Alerta si no hay cargos */}
        {cargos.length === 0 && !loadingCargos && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <DollarOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4} style={{ color: '#bfbfbf' }}>
              No hay cargos registrados
            </Title>
            <Text type="secondary">
              Puedes añadir un cargo haciendo clic en "Añadir Cargo"
            </Text>
          </div>
        )}
      </Modal>

      {/* Modal para crear/editar cargo */}
      <Modal
        title={editandoCargo ? 'Editar Cargo' : 'Nuevo Cargo'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancelar
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleGuardarCargo}
            icon={<SaveOutlined />}
          >
            {editandoCargo ? 'Actualizar' : 'Guardar'}
          </Button>
        ]}
      >
        {/* Mostrar cálculo de días y total en la parte superior */}
        <Alert
          message={`Días de aplicación: ${calcularDias()} días | Total: ${calcularTotal()} €`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={form}
          layout="vertical"
          disabled={editandoCargo && (editandoCargo.Facturado || editandoCargo.nfactura > 0)}
          onFieldsChange={handleFechaChange}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Codigo"
                label="Código de Cargo"
                rules={[{ required: true, message: 'El código es obligatorio' }]}
              >
                <Select
                  showSearch
                  placeholder="Seleccione código de cargo"
                  onChange={handleCambioCodigo}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {cargosMaestros.map(cargo => (
                    <Option key={cargo.codigo} value={cargo.codigo}>
                      {cargo.codigo} - {cargo.descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ required: true, message: 'La descripción es obligatoria' }]}
              >
                <Input placeholder="Descripción del cargo" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="PVP"
                label="PVP (€)"
                rules={[
                  { required: true, message: 'El PVP es obligatorio' },
                  { type: 'number', min: 0.01, message: 'El PVP debe ser mayor a 0' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  step={0.01}
                  precision={2}
                  formatter={value => `${value} €`}
                  parser={value => value.replace(' €', '')}
                />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="NumCargos"
                label="Cantidad"
                rules={[
                  { required: true, message: 'La cantidad es obligatoria' },
                  { type: 'number', min: 1, message: 'La cantidad debe ser al menos 1' }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  step={1}
                />
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="Seccion"
                label="Sección"
              >
                <Input placeholder="HOT" disabled />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Fechas de Aplicación</Divider>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Fecha"
                label="Fecha Desde"
                rules={[{ required: true, message: 'La fecha inicial es obligatoria' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  onChange={handleFechaChange}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="FechaHasta"
                label="Fecha Hasta"
                rules={[{ required: true, message: 'La fecha final es obligatoria' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  onChange={handleFechaChange}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Mostrar advertencia de fechas si existe */}
          {fechaWarning && (
            <Alert
              message={fechaWarning.message}
              type={fechaWarning.type}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Divider>Asignación</Divider>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="habitacion"
                label="Habitación"
              >
                <Select
                  showSearch
                  placeholder="Seleccione habitación"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  onChange={handleCambioHabitacion}
                >
                  {habitacionesReserva.map(hab => (
                    <Option key={hab} value={hab}>
                      Habitación {hab}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="NLinea"
                label="Línea"
                rules={[{ required: true, message: 'La línea es obligatoria' }]}
              >
                <Select
                  placeholder="Seleccione línea"
                  allowClear
                  onChange={handleCambioLinea}
                >
                  {[...new Set(lineasReserva.map(l => l.Linea))].sort((a, b) => a - b).map(linea => {
                    const lineaData = lineasReserva.find(l => l.Linea === linea);
                    const fechaIni = lineaData?.Entrada ? dayjs(lineaData.Entrada).format('DD/MM/YY') : '';
                    const fechaFin = lineaData?.Salida ? dayjs(lineaData.Salida).format('DD/MM/YY') : '';
                    return (
                      <Option key={linea} value={linea}>
                        Línea {linea} ({fechaIni} - {fechaFin})
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="Ocupante"
                label="Ocupante"
              >
                <Select
                  placeholder="Seleccione ocupante"
                  allowClear
                >
                  {[1, 2, 3, 4, 5].map(ocupante => (
                    <Option key={ocupante} value={ocupante}>
                      Ocupante {ocupante}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider>Configuración</Divider>
          
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="TipoIVA"
                label="Tipo IVA"
              >
                <Select 
                  placeholder="Seleccione tipo IVA"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {tiposIVA.map(tipo => (
                    <Option key={tipo.Codigo} value={tipo.Codigo}>
                      {tipo.Descripcion} ({tipo.ValorActual}%)
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="Aplicado"
                label="IVA Aplicado"
              >
                <Select placeholder="Seleccione aplicación">
                  <Option value="I">Incluido</Option>
                  <Option value="E">Exento</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={8}>
              <Form.Item
                name="Serie"
                label="Serie"
              >
                <Select placeholder="Seleccione serie">
                  {series.map(serie => (
                    <Option key={serie.Codigo} value={serie.Codigo}>
                      {serie.Codigo} - {serie.Descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Observaciones"
                label="Observaciones"
              >
                <Input.TextArea
                  rows={3}
                  placeholder="Observaciones del cargo..."
                  maxLength={500}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item label="Opciones" style={{ marginTop: 30 }}>
                <Space direction="vertical">
                  <Form.Item name="CalculaDto" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Calcula Dto" unCheckedChildren="No Dto" />
                  </Form.Item>
                  <Form.Item name="Traspasa" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Traspasa" unCheckedChildren="No Traspasa" />
                  </Form.Item>
                  <Form.Item name="Facturado" valuePropName="checked" noStyle>
                    <Switch checkedChildren="Facturado" unCheckedChildren="No Fact." />
                  </Form.Item>
                </Space>
              </Form.Item>
            </Col>
          </Row>

          {editandoCargo && (editandoCargo.Facturado || editandoCargo.nfactura > 0) && (
            <Alert
              message={
                editandoCargo.Facturado 
                  ? "Este cargo ya está facturado y no se puede modificar"
                  : "Este cargo está en proceso de facturación"
              }
              type="warning"
              showIcon
              icon={<WarningOutlined />}
            />
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CargosReserva;